<script setup lang="ts">
import {
  EditableArea,
  EditableCancelTrigger,
  EditableInput,
  EditablePreview,
  EditableRoot,
  EditableSubmitTrigger,
} from "reka-ui";
import { useMachine } from "@xstate/vue";
import { computed, onMounted, useTemplateRef } from "vue";

import { SummaryNotesItemLogic } from "@/machines/summary-notes-item-machine";
import type { SummaryNote } from "@/models/SummaryNote";

withDefaults(defineProps<{
  fileReference: string;
  lastPosition: number;
  theme: string;
}>(), {  })
const emit = defineEmits<{
  (e: "stopAdding"): void;
  (e: "refreshList", dp: SummaryNote): void;
}>();

const editableRootRef = useTemplateRef<InstanceType<typeof EditableRoot>>("editable-root");
const editableTextareaRef = useTemplateRef<HTMLTextAreaElement>("editable-textarea");

const { actorRef, send, snapshot } = useMachine(SummaryNotesItemLogic, {
  input: {
    params: {
      fileReference: __props.fileReference,
      summaryText: "",
      reference: undefined,
      awaitingCitations: false,
      evidenceTheme: __props.theme,
      position: __props.lastPosition + 1,
    },
    pollCitations: false,
  },
});

actorRef.on("COMMIT_FAILED", () => editableRootRef.value?.edit());
actorRef.on("COMMIT_SUCCESS", ({ summaryNote }) => emit("refreshList", summaryNote));

const noteError = computed(() => snapshot.value.context.error);

const state = computed(() => {
  if (snapshot.value.matches("idle")) return "IDLE";
  if (snapshot.value.matches("committing")) return "COMMITTING";
  if (snapshot.value.matches("editing")) return "EDITING";
  return undefined;
});

function onSubmit(value: string | null | undefined) {
  const isNotValid = !(editableTextareaRef.value?.reportValidity() ?? true);
  value = value?.trim() ?? "";

  if (isNotValid || value.length < 1) editableRootRef.value?.edit();
  else send({ type: "COMMIT", value });
}

function handleStateChange(state: "edit" | "cancel" | "submit") {
  switch (state) {
    case "edit":
      send({ type: "START_EDITING" });
      break;
    case "cancel":
      emit("stopAdding");
      break;
  }
}

onMounted(() => editableRootRef.value?.edit());
</script>
<template>
  <div class="tw-grid tw-grid-cols-subgrid tw-items-end tw-gap-1">
    <EditableRoot
      ref="editable-root"
      select-on-focus
      submit-mode="enter"
      activation-mode="dblclick"
      class="tw-col-start-2 tw-flex tw-items-center tw-gap-2"
      :readonly="state === 'COMMITTING'"
      @interact-outside.prevent
      @submit="onSubmit"
      @update:state="handleStateChange"
    >
      <EditableArea
        class="tw-flex tw-min-h-[--height] tw-flex-1 tw-flex-col tw-justify-center [--height:theme(spacing.8)]"
      >
        <EditablePreview />
        <EditableInput as-child>
          <textarea
            ref="editable-textarea"
            required
            :class="{ 'tw-border-red-400 tw-outline-red-400': noteError != null }"
            class="tw-size-full tw-min-h-[--height] tw-rounded-md tw-border tw-border-gray-400 tw-px-2.5 tw-leading-[--height] [field-sizing:content]"
          />
        </EditableInput>
        <div v-if="state === 'COMMITTING'" class="tw-flex tw-items-center tw-gap-1 tw-text-blue-500">
          <i class="fas fa-sm fa-spin fa-circle-notch"></i>
          Validating
        </div>
        <div v-else-if="noteError != null" class="tw-col-span-full tw-text-red-400">
          <i class="fas fa-triangle-exclamation" />
          {{ noteError.content }}
        </div>
      </EditableArea>
      <div v-if="state === 'EDITING'" class="tw-flex tw-items-center tw-gap-1">
        <EditableSubmitTrigger
          class="tw-rounded-md tw-px-2 tw-py-1 tw-text-green-600 tw-underline tw-underline-offset-2 hover:tw-bg-green-800/75 hover:tw-text-white"
        >
          Save
        </EditableSubmitTrigger>
        <EditableCancelTrigger
          class="tw-rounded-md tw-px-2 tw-py-1 tw-text-red-600 tw-underline tw-underline-offset-2 hover:tw-bg-red-800/75 hover:tw-text-white"
        >
          Cancel
        </EditableCancelTrigger>
      </div>
    </EditableRoot>
  </div>
</template>
